import type { ModuleId, OverrideMode } from '@tahi/core';
import { ApiError, apiFetch } from '../../data/api';
import type { ModuleView, Role } from '../../data/me';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  createdAt: string;
  modules: ModuleView[];
}

/** A row of the invites table (GET returns every row, including revoked and accepted ones). */
export interface InviteRow {
  id: string;
  email: string;
  invitedBy: string | null;
  note: string | null;
  createdAt: string;
  acceptedAt: string | null;
  acceptedUserId: string | null;
  revokedAt: string | null;
  /** When the current link stops working; null on rows made before invite links existed. */
  expiresAt: string | null;
}

/** POST /api/admin/invites: the row plus its one-time link (never returned again). */
export type NewInvite = InviteRow & { url: string };

export type InviteStatus = 'pending' | 'accepted' | 'revoked' | 'expired';

/** Mirrors the Worker: an open invite with no expiry, or one in the past, has no working link. */
export function inviteStatus(row: InviteRow, now = Date.now()): InviteStatus {
  if (row.acceptedAt) return 'accepted';
  if (row.revokedAt) return 'revoked';
  if (!row.expiresAt || Date.parse(row.expiresAt) <= now) return 'expired';
  return 'pending';
}

/** A row of audit_log. Ids, not emails; the panel maps them with the users list. */
export interface AuditRow {
  id: string;
  actorId: string;
  targetUserId: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  before: unknown;
  after: unknown;
  at: string;
}

export interface AppSettings {
  inviteOnly: boolean;
  billingLive: boolean;
}

const enc = encodeURIComponent;
export const AUDIT_PAGE = 50;

export const adminApi = {
  listUsers: () => apiFetch<{ users: AdminUser[] }>('/api/admin/users').then((r) => r.users),
  setOverride: (userId: string, moduleId: ModuleId, mode: OverrideMode) =>
    apiFetch<{ userId: string; moduleId: ModuleId; mode: OverrideMode }>(
      `/api/admin/users/${enc(userId)}/modules/${moduleId}`,
      { method: 'PUT', json: { mode } },
    ),
  createResetLink: (userId: string) =>
    apiFetch<{ url: string; expiresAt: string }>(`/api/admin/users/${enc(userId)}/reset-link`, {
      method: 'POST',
      json: {},
    }),
  /** POST /api/admin/users/:id/sample-data; 409 `has_sample_data` unless `replace`. */
  fillSampleData: (userId: string, replace = false) =>
    apiFetch<{ entries: number; replaced: number }>(
      `/api/admin/users/${enc(userId)}/sample-data${replace ? '?replace=1' : ''}`,
      { method: 'POST', json: {} },
    ),
  listInvites: () =>
    apiFetch<{ invites: InviteRow[] }>('/api/admin/invites').then((r) => r.invites),
  addInvite: (email: string) =>
    apiFetch<NewInvite>('/api/admin/invites', { method: 'POST', json: { email } }),
  revokeInvite: (id: string) =>
    apiFetch<null>(`/api/admin/invites/${enc(id)}`, { method: 'DELETE' }),
  listAudit: (before?: string) =>
    apiFetch<{ entries: AuditRow[]; nextBefore: string | null }>(
      `/api/admin/audit?limit=${AUDIT_PAGE}${before ? `&before=${enc(before)}` : ''}`,
    ),
  getSettings: () => apiFetch<AppSettings>('/api/admin/settings'),
  updateSettings: (patch: Partial<AppSettings>) =>
    apiFetch<AppSettings>('/api/admin/settings', { method: 'PUT', json: patch }),
};

/** Tahi routes send `{ error, message }`; show the message, or a plain line when offline. */
export function adminErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'forbidden') return "You don't have access to this.";
    // A code with no message (not_found, http_500, ...) isn't worth showing.
    return err.message !== err.code ? err.message : 'Something went wrong. Try again in a moment.';
  }
  return "Couldn't reach the server. The admin console needs a connection.";
}
